import { useMemo } from 'react';

interface CostEstimationParams {
  contractCount: number;
  analysisType: 'single' | 'multiple' | 'bulk';
  isCritical?: boolean;
}

interface AdvancedCostEstimationParams {
  analysisType: 'single' | 'multiple' | 'bulk';
  // Für Provider-basierte Analyse
  selectedProviders?: Array<{
    id: string;
    contractPackages?: Array<{
      status: string;
      isCritical?: boolean;
    }>;
  }>;
  // Für Contract-basierte Analyse
  selectedContracts?: Array<{
    id: string;
    status: string;
    isCritical?: boolean;
  }>;
  // Für einzelne Analyse
  singleContract?: {
    isCritical?: boolean;
  };
}

interface CostEstimation {
  estimatedCost: number;
  baseCostPerContract: number;
  criticalMultiplier: number;
  totalAnalyzableContracts: number;
  breakdown: {
    critical: number;
    nonCritical: number;
  };
}

// Kostenschätzung basierend auf bisherigen Analysedaten  
const COST_BASE = {
  // Neue Preisstruktur
  simpleContract: 2.50,    // Einfache Dienstleistungen: 2,50€
  criticalContract: 6.00,  // Kritische Dienstleistungen: 6,00€
  // Kein Bulk-Discount mehr
  bulkDiscount: 1.0        // Kein Rabatt
};

// Hilfsfunktion: Prüfe ob Status analysierbar ist
const isAnalyzableStatus = (status: string): boolean => {
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === 'open' || 
         normalizedStatus === 'failed' ||
         normalizedStatus === 'error';
};

// Legacy Hook für einfache Kostenschätzung (backward compatibility)
export const useAnalysisCost = (params: CostEstimationParams): CostEstimation => {
  const estimation = useMemo(() => {
    const { contractCount, analysisType, isCritical = true } = params;
    
    let costPerContract = isCritical ? COST_BASE.criticalContract : COST_BASE.simpleContract;
    
    // Kein Bulk-Rabatt mehr
    
    // Berechne Gesamtkosten
    const totalCost = costPerContract * contractCount;
    
    return {
      estimatedCost: Math.round(totalCost * 100) / 100,
      baseCostPerContract: COST_BASE.simpleContract,  // Zeige einfachen Preis als Basis
      criticalMultiplier: COST_BASE.criticalContract / COST_BASE.simpleContract,
      totalAnalyzableContracts: contractCount,
      breakdown: {
        critical: isCritical ? contractCount : 0,
        nonCritical: isCritical ? 0 : contractCount
      }
    };
  }, [params.contractCount, params.analysisType, params.isCritical]);
  
  return estimation;
};

// Neuer fortgeschrittener Hook für präzise Kostenschätzung
export const useAdvancedAnalysisCost = (params: AdvancedCostEstimationParams): CostEstimation => {
  const estimation = useMemo(() => {
    const { analysisType, selectedProviders, selectedContracts, singleContract } = params;
    
    let analyzableContracts: Array<{ isCritical: boolean }> = [];
    
    // Sammle alle analysierbaren Verträge je nach Typ
    if (analysisType === 'single' && singleContract) {
      analyzableContracts = [{ isCritical: singleContract.isCritical ?? true }];
    } else if (analysisType === 'multiple' && selectedContracts) {
      analyzableContracts = selectedContracts
        .filter(contract => isAnalyzableStatus(contract.status))
        .map(contract => ({ isCritical: contract.isCritical ?? true }));
    } else if (analysisType === 'bulk' && selectedProviders) {
      // Sammle alle analysierbaren Verträge aus allen ausgewählten Providern
      selectedProviders.forEach(provider => {
        if (provider.contractPackages) {
          provider.contractPackages
            .filter(contractPackage => isAnalyzableStatus(contractPackage.status))
            .forEach(contractPackage => {
              analyzableContracts.push({ isCritical: contractPackage.isCritical ?? true });
            });
        }
      });
    }
    
    // Berechne Breakdown
    const criticalContracts = analyzableContracts.filter(c => c.isCritical).length;
    const nonCriticalContracts = analyzableContracts.filter(c => !c.isCritical).length;
    const totalContracts = analyzableContracts.length;
    
    // Neue Preisstruktur - keine Rabatte
    const criticalCost = criticalContracts * COST_BASE.criticalContract;
    const nonCriticalCost = nonCriticalContracts * COST_BASE.simpleContract;
    const totalCost = criticalCost + nonCriticalCost;
    
    return {
      estimatedCost: Math.round(totalCost * 100) / 100,
      baseCostPerContract: COST_BASE.simpleContract,  // Zeige einfachen Preis als Basis
      criticalMultiplier: COST_BASE.criticalContract / COST_BASE.simpleContract,
      totalAnalyzableContracts: totalContracts,
      breakdown: {
        critical: criticalContracts,
        nonCritical: nonCriticalContracts
      }
    };
  }, [params]);
  
  return estimation;
}; 